// Mounts React components onto the markup emitted by ReactHelper#react_component.
// This is the replacement for react-rails' react_ujs.
//
// Note on the readyState check below: a bare DOMContentLoaded listener silently
// mounts nothing if the bundle finishes executing after that event has already
// fired. react_ujs guarded against this too.
//
// Note on createRoot: React 18's createRoot enables automatic batching, which
// defers re-renders that React 16 flushed synchronously. That can reorder
// updates relative to Capybara/Selenium interactions. The legacy
// ReactDOM.render keeps React 16 timing, but swapping it in caused every
// component to stop mounting, so we're staying on createRoot until that's
// understood. See docs/rails-8-port.md.
import React from 'react'
import { createRoot } from 'react-dom/client'
import * as components from './components'

const MOUNTED = '_reactRoot'

function mountAll(root = document) {
  root.querySelectorAll('[data-react-component]').forEach((el) => {
    if (el[MOUNTED]) return

    const name = el.dataset.reactComponent
    const Component = components[name]

    if (!Component) {
      console.error(`react_component: no component registered as "${name}". ` +
        'Add it to app/javascript/components/index.js')
      return
    }

    let props = {}
    if (el.dataset.reactProps) {
      try {
        props = JSON.parse(el.dataset.reactProps)
      } catch (e) {
        console.error(`react_component: bad props JSON for "${name}"`, e)
        return
      }
    }

    // Isolated so one component blowing up doesn't stop the others mounting.
    try {
      el[MOUNTED] = createRoot(el)
      el[MOUNTED].render(React.createElement(Component, props))
    } catch (e) {
      el[MOUNTED] = null
      console.error(`react_component: "${name}" failed to mount`, e)
    }
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll())
} else {
  mountAll()
}

// Exposed so inline scripts in views (rendered via `yield :javascripts`) can
// mount components added to the DOM after page load.
window.mountReactComponents = mountAll

export default mountAll
